import React, { useState, useEffect } from 'react';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Badge } from '@/components/ui/badge';
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle, DialogFooter } from '@/components/ui/dialog';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent, AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle } from '@/components/ui/alert-dialog';
import { Switch } from '@/components/ui/switch';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { useToast } from '@/hooks/use-toast';
import {
  Plus,
  Edit,
  Trash2,
  RefreshCw,
  Tag,
  Bell,
  CreditCard,
  Settings,
  ChevronRight,
  TrendingUp,
  TrendingDown,
  CheckCircle,
  AlertCircle,
  Info,
  GripVertical,
  FileText,
  Home,
  Users,
  Megaphone,
  Laptop,
  Building,
  Phone,
  Wifi,
  ShoppingCart,
  Package,
  Briefcase,
  ShoppingBag,
  MessageSquare,
  DollarSign
} from 'lucide-react';
import { configuracoesFinanceiroService } from '@/services';
import { DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuTrigger } from '@/components/ui/dropdown-menu';

const Configuracoes = () => {
  const { toast } = useToast();

  // Estados principais
  const [loading, setLoading] = useState(true);
  const [categorias, setCategorias] = useState([]);
  const [metodosPagamento, setMetodosPagamento] = useState([]);
  const [configAlertas, setConfigAlertas] = useState(null);
  const [estatisticas, setEstatisticas] = useState(null);

  // Estados de modais
  const [categoriaModalOpen, setCategoriaModalOpen] = useState(false);
  const [alertasModalOpen, setAlertasModalOpen] = useState(false);
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [categoriaSelecionada, setCategoriaSelecionada] = useState(null);
  const [modoEdicao, setModoEdicao] = useState(false);

  // Estados do formulário de categoria
  const [categoriaFormData, setCategoriaFormData] = useState({
    nome: '',
    tipo: 'despesa',
    cor: '#6366f1',
    icone: 'Tag',
    descricao: ''
  });

  // Estados do formulário de alertas
  const [alertasFormData, setAlertasFormData] = useState({
    receitas: {
      vencimento_dias_antes: [3, 7, 15],
      atraso_dias_apos: [1, 3, 7, 15],
      notificar_recebimento: true,
      canais: ['email', 'sistema']
    },
    despesas: {
      vencimento_dias_antes: [3, 7, 15],
      atraso_dias_apos: [1, 3, 7],
      canais: ['email', 'sistema']
    },
    contratos: {
      renovacao_dias_antes: [90, 60, 30, 15],
      reajuste_dias_antes: [30, 15, 7],
      canais: ['email', 'sistema']
    },
    resumo_diario: false,
    resumo_semanal: false,
    resumo_mensal: true,
    horario_envio: '08:00'
  });

  // Cores disponíveis
  const coresDisponiveis = [
    { value: '#ef4444', label: 'Vermelho' },
    { value: '#f97316', label: 'Laranja' },
    { value: '#eab308', label: 'Amarelo' },
    { value: '#84cc16', label: 'Verde Limão' },
    { value: '#10b981', label: 'Verde' },
    { value: '#06b6d4', label: 'Ciano' },
    { value: '#6366f1', label: 'Índigo' },
    { value: '#8b5cf6', label: 'Roxo' },
    { value: '#ec4899', label: 'Rosa' },
    { value: '#f59e0b', label: 'Âmbar' }
  ];

  // Ícones disponíveis (Lucide)
  const iconesDisponiveis = [
    'Tag', 'Home', 'Users', 'FileText', 'Megaphone', 'Laptop', 
    'Building', 'Phone', 'Wifi', 'ShoppingCart', 'Package',
    'Briefcase', 'ShoppingBag', 'MessageSquare', 'DollarSign'
  ];

  useEffect(() => {
    loadConfiguracoes();
  }, []);

  const loadConfiguracoes = async () => {
    setLoading(true);
    try {
      const [categoriasData, metodosData, alertasData, statsData] = await Promise.all([
        configuracoesFinanceiroService.getCategorias(),
        configuracoesFinanceiroService.getMetodosPagamento(),
        configuracoesFinanceiroService.getConfiguracoesAlertas(),
        configuracoesFinanceiroService.getEstatisticasUso()
      ]);

      setCategorias(categoriasData.categorias || []);
      setMetodosPagamento(metodosData.metodos || []);
      setConfigAlertas(alertasData);
      setEstatisticas(statsData);

      // Popular formulário de alertas se houver dados
      if (alertasData && alertasData.receitas) {
        setAlertasFormData(alertasData);
      }
    } catch (error) {
      toast({
        title: 'Erro',
        description: error,
        variant: 'destructive',
      });
    } finally {
      setLoading(false);
    }
  };

  const handleCriarCategoriasPadrao = async () => {
    try {
      await configuracoesFinanceiroService.criarCategoriasPadrao();
      toast({
        title: 'Sucesso',
        description: 'Categorias padrão criadas com sucesso',
      });
      loadConfiguracoes();
    } catch (error) {
      toast({
        title: 'Erro',
        description: error,
        variant: 'destructive',
      });
    }
  };

  const handleCriarMetodosPadrao = async () => {
    try {
      await configuracoesFinanceiroService.criarMetodosPadrao();
      toast({
        title: 'Sucesso',
        description: 'Métodos de pagamento padrão criados com sucesso',
      });
      loadConfiguracoes();
    } catch (error) {
      toast({
        title: 'Erro',
        description: error,
        variant: 'destructive',
      });
    }
  };

  const handleSubmitCategoria = async (e) => {
    e.preventDefault();
    
    try {
      if (!categoriaFormData.nome) {
        toast({
          title: 'Erro',
          description: 'Informe o nome da categoria',
          variant: 'destructive',
        });
        return;
      }

      if (modoEdicao && categoriaSelecionada) {
        const categoriaId = categoriaSelecionada.id || categoriaSelecionada._id;
        if (!categoriaId) {
          toast({
            title: 'Erro',
            description: 'ID da categoria não encontrado',
            variant: 'destructive',
          });
          return;
        }

        await configuracoesFinanceiroService.updateCategoria(categoriaId, categoriaFormData);
        toast({
          title: 'Sucesso',
          description: 'Categoria atualizada com sucesso',
        });
      } else {
        await configuracoesFinanceiroService.createCategoria(categoriaFormData);
        toast({
          title: 'Sucesso',
          description: 'Categoria criada com sucesso',
        });
      }

      setCategoriaModalOpen(false);
      resetCategoriaForm();
      loadConfiguracoes();
    } catch (error) {
      toast({
        title: 'Erro',
        description: error,
        variant: 'destructive',
      });
    }
  };

  const handleSubmitAlertas = async (e) => {
    e.preventDefault();
    
    try {
      await configuracoesFinanceiroService.salvarConfiguracoesAlertas(alertasFormData);
      toast({
        title: 'Sucesso',
        description: 'Configurações de alertas salvas com sucesso',
      });
      setAlertasModalOpen(false);
      loadConfiguracoes();
    } catch (error) {
      toast({
        title: 'Erro',
        description: error,
        variant: 'destructive',
      });
    }
  };

  const handleEditCategoria = (categoria) => {
    setCategoriaSelecionada(categoria);
    setModoEdicao(true);
    setCategoriaFormData({
      nome: categoria.nome || '',
      tipo: categoria.tipo || 'despesa',
      cor: categoria.cor || '#6366f1',
      icone: categoria.icone || 'Tag',
      descricao: categoria.descricao || ''
    });
    setCategoriaModalOpen(true);
  };

  const handleDeleteCategoria = async () => {
    try {
      const categoriaId = categoriaSelecionada.id || categoriaSelecionada._id;
      if (!categoriaId) {
        toast({
          title: 'Erro',
          description: 'ID da categoria não encontrado',
          variant: 'destructive',
        });
        return;
      }

      await configuracoesFinanceiroService.deleteCategoria(categoriaId, false);
      toast({
        title: 'Sucesso',
        description: 'Categoria removida com sucesso',
      });
      setDeleteDialogOpen(false);
      setCategoriaSelecionada(null);
      loadConfiguracoes();
    } catch (error) {
      toast({
        title: 'Erro',
        description: error,
        variant: 'destructive',
      });
    }
  };

  const resetCategoriaForm = () => {
    setCategoriaFormData({
      nome: '',
      tipo: 'despesa',
      cor: '#6366f1',
      icone: 'Tag',
      descricao: ''
    });
    setModoEdicao(false);
    setCategoriaSelecionada(null);
  };

  const getIconComponent = (iconName) => {
    const icons = {
      Tag, Home: TrendingDown, Users: TrendingUp, FileText, Megaphone: Bell,
      Laptop: CreditCard, Building: Settings, Phone: Info, Wifi: CheckCircle,
      ShoppingCart: AlertCircle, Package: Tag, Briefcase: Tag, ShoppingBag: Tag,
      MessageSquare: Tag, DollarSign: Tag
    };
    const Icon = icons[iconName] || Tag;
    return <Icon className="h-4 w-4" />;
  };

  // Filtrar categorias por tipo
  const categoriasReceita = categorias.filter(c => c.tipo === 'receita');
  const categoriasDespesa = categorias.filter(c => c.tipo === 'despesa');

  // Loading state
  if (loading) {
    return (
      <div className="flex items-center justify-center h-96">
        <div className="text-center">
          <RefreshCw className="h-8 w-8 animate-spin mx-auto mb-4 text-purple-600" />
          <p className="text-gray-500">Carregando configurações...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div>
        <h1 className="text-3xl font-bold text-gray-900">Configurações Financeiras</h1>
        <p className="text-gray-500 mt-1">Gerencie categorias, alertas e preferências do módulo financeiro</p>
      </div>

      {/* Estatísticas de Uso */}
      {estatisticas && (
        <Card>
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <Info className="h-5 w-5 text-purple-600" />
              Estatísticas de Uso
            </CardTitle>
            <CardDescription>Visão geral do módulo financeiro</CardDescription>
          </CardHeader>
          <CardContent>
            <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
              <div className="text-center p-4 bg-purple-50 rounded-lg">
                <p className="text-3xl font-bold text-purple-600">{estatisticas.totais?.contratos || 0}</p>
                <p className="text-sm text-gray-600 mt-1">Contratos</p>
              </div>
              <div className="text-center p-4 bg-green-50 rounded-lg">
                <p className="text-3xl font-bold text-green-600">{estatisticas.totais?.receitas || 0}</p>
                <p className="text-sm text-gray-600 mt-1">Receitas</p>
              </div>
              <div className="text-center p-4 bg-red-50 rounded-lg">
                <p className="text-3xl font-bold text-red-600">{estatisticas.totais?.despesas || 0}</p>
                <p className="text-sm text-gray-600 mt-1">Despesas</p>
              </div>
              <div className="text-center p-4 bg-blue-50 rounded-lg">
                <p className="text-3xl font-bold text-blue-600">{estatisticas.totais?.categorias || 0}</p>
                <p className="text-sm text-gray-600 mt-1">Categorias</p>
              </div>
            </div>
            {estatisticas.uso?.dias_uso > 0 && (
              <p className="text-center text-sm text-gray-500 mt-4">
                Módulo em uso há {estatisticas.uso.dias_uso} dias
              </p>
            )}
          </CardContent>
        </Card>
      )}

      {/* Tabs */}
      <Tabs defaultValue="categorias" className="space-y-6">
        <TabsList className="grid w-full grid-cols-3">
          <TabsTrigger value="categorias">
            <Tag className="h-4 w-4 mr-2" />
            Categorias
          </TabsTrigger>
          <TabsTrigger value="alertas">
            <Bell className="h-4 w-4 mr-2" />
            Alertas
          </TabsTrigger>
          <TabsTrigger value="metodos">
            <CreditCard className="h-4 w-4 mr-2" />
            Métodos de Pagamento
          </TabsTrigger>
        </TabsList>

        {/* ABA DE CATEGORIAS */}
        <TabsContent value="categorias" className="space-y-6">
          <div className="flex items-center justify-between">
            <div>
              <h2 className="text-xl font-semibold">Categorias Financeiras</h2>
              <p className="text-sm text-gray-500">Organize receitas e despesas por categorias</p>
            </div>
            <div className="flex gap-2">
              <Button variant="outline" onClick={handleCriarCategoriasPadrao}>
                <CheckCircle className="h-4 w-4 mr-2" />
                Criar Padrões
              </Button>
              <Button onClick={() => { resetCategoriaForm(); setCategoriaModalOpen(true); }}>
                <Plus className="h-4 w-4 mr-2" />
                Nova Categoria
              </Button>
            </div>
          </div>

          {/* Categorias de Despesa */}
          <Card>
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <TrendingDown className="h-5 w-5 text-red-600" />
                Categorias de Despesa
              </CardTitle>
              <CardDescription>
                {categoriasDespesa.length} categorias cadastradas
              </CardDescription>
            </CardHeader>
            <CardContent>
              {categoriasDespesa.length === 0 ? (
                <div className="text-center py-8 text-gray-500">
                  <Tag className="h-12 w-12 mx-auto mb-2 text-gray-300" />
                  <p>Nenhuma categoria de despesa cadastrada</p>
                </div>
              ) : (
                <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
                  {categoriasDespesa.map((categoria) => (
                    <Card key={categoria.id} className="hover:shadow-md transition-shadow">
                      <CardContent className="pt-6">
                        <div className="flex items-start justify-between">
                          <div className="flex items-center gap-3 flex-1">
                            <div
                              className="h-10 w-10 rounded-lg flex items-center justify-center"
                              style={{ backgroundColor: categoria.cor + '20' }}
                            >
                              <span style={{ color: categoria.cor }}>
                                {getIconComponent(categoria.icone)}
                              </span>
                            </div>
                            <div className="flex-1">
                              <p className="font-semibold text-gray-900">{categoria.nome}</p>
                              {categoria.descricao && (
                                <p className="text-xs text-gray-500 mt-1">{categoria.descricao}</p>
                              )}
                              {!categoria.ativa && (
                                <Badge variant="secondary" className="mt-2">Inativa</Badge>
                              )}
                            </div>
                          </div>
                          <DropdownMenu>
                            <DropdownMenuTrigger asChild>
                              <Button variant="ghost" size="sm">
                                <Settings className="h-4 w-4" />
                              </Button>
                            </DropdownMenuTrigger>
                            <DropdownMenuContent align="end">
                              <DropdownMenuItem onClick={() => handleEditCategoria(categoria)}>
                                <Edit className="h-4 w-4 mr-2" />
                                Editar
                              </DropdownMenuItem>
                              <DropdownMenuItem
                                onClick={() => {
                                  setCategoriaSelecionada(categoria);
                                  setDeleteDialogOpen(true);
                                }}
                                className="text-red-600"
                              >
                                <Trash2 className="h-4 w-4 mr-2" />
                                Remover
                              </DropdownMenuItem>
                            </DropdownMenuContent>
                          </DropdownMenu>
                        </div>
                      </CardContent>
                    </Card>
                  ))}
                </div>
              )}
            </CardContent>
          </Card>

          {/* Categorias de Receita */}
          <Card>
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <TrendingUp className="h-5 w-5 text-green-600" />
                Categorias de Receita
              </CardTitle>
              <CardDescription>
                {categoriasReceita.length} categorias cadastradas
              </CardDescription>
            </CardHeader>
            <CardContent>
              {categoriasReceita.length === 0 ? (
                <div className="text-center py-8 text-gray-500">
                  <Tag className="h-12 w-12 mx-auto mb-2 text-gray-300" />
                  <p>Nenhuma categoria de receita cadastrada</p>
                </div>
              ) : (
                <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
                  {categoriasReceita.map((categoria) => (
                    <Card key={categoria.id} className="hover:shadow-md transition-shadow">
                      <CardContent className="pt-6">
                        <div className="flex items-start justify-between">
                          <div className="flex items-center gap-3 flex-1">
                            <div
                              className="h-10 w-10 rounded-lg flex items-center justify-center"
                              style={{ backgroundColor: categoria.cor + '20' }}
                            >
                              <span style={{ color: categoria.cor }}>
                                {getIconComponent(categoria.icone)}
                              </span>
                            </div>
                            <div className="flex-1">
                              <p className="font-semibold text-gray-900">{categoria.nome}</p>
                              {categoria.descricao && (
                                <p className="text-xs text-gray-500 mt-1">{categoria.descricao}</p>
                              )}
                              {!categoria.ativa && (
                                <Badge variant="secondary" className="mt-2">Inativa</Badge>
                              )}
                            </div>
                          </div>
                          <DropdownMenu>
                            <DropdownMenuTrigger asChild>
                              <Button variant="ghost" size="sm">
                                <Settings className="h-4 w-4" />
                              </Button>
                            </DropdownMenuTrigger>
                            <DropdownMenuContent align="end">
                              <DropdownMenuItem onClick={() => handleEditCategoria(categoria)}>
                                <Edit className="h-4 w-4 mr-2" />
                                Editar
                              </DropdownMenuItem>
                              <DropdownMenuItem
                                onClick={() => {
                                  setCategoriaSelecionada(categoria);
                                  setDeleteDialogOpen(true);
                                }}
                                className="text-red-600"
                              >
                                <Trash2 className="h-4 w-4 mr-2" />
                                Remover
                              </DropdownMenuItem>
                            </DropdownMenuContent>
                          </DropdownMenu>
                        </div>
                      </CardContent>
                    </Card>
                  ))}
                </div>
              )}
            </CardContent>
          </Card>
        </TabsContent>

        {/* ABA DE ALERTAS */}
        <TabsContent value="alertas" className="space-y-6">
          <div className="flex items-center justify-between">
            <div>
              <h2 className="text-xl font-semibold">Configurações de Alertas</h2>
              <p className="text-sm text-gray-500">Personalize notificações e lembretes</p>
            </div>
            <Button onClick={() => setAlertasModalOpen(true)}>
              <Settings className="h-4 w-4 mr-2" />
              Configurar Alertas
            </Button>
          </div>

          <Card>
            <CardHeader>
              <CardTitle>Alertas Ativos</CardTitle>
              <CardDescription>Suas configurações atuais de notificações</CardDescription>
            </CardHeader>
            <CardContent className="space-y-6">
              {/* Receitas */}
              <div>
                <h3 className="font-semibold mb-3 flex items-center gap-2">
                  <TrendingUp className="h-4 w-4 text-green-600" />
                  Alertas de Receitas
                </h3>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4 pl-6">
                  <div className="space-y-2">
                    <p className="text-sm text-gray-600">Vencimento (dias antes)</p>
                    <div className="flex gap-2">
                      {configAlertas?.receitas?.vencimento_dias_antes?.map((dias) => (
                        <Badge key={dias} variant="secondary">{dias}d</Badge>
                      ))}
                    </div>
                  </div>
                  <div className="space-y-2">
                    <p className="text-sm text-gray-600">Atraso (dias após)</p>
                    <div className="flex gap-2">
                      {configAlertas?.receitas?.atraso_dias_apos?.map((dias) => (
                        <Badge key={dias} variant="destructive">{dias}d</Badge>
                      ))}
                    </div>
                  </div>
                </div>
              </div>

              {/* Despesas */}
              <div>
                <h3 className="font-semibold mb-3 flex items-center gap-2">
                  <TrendingDown className="h-4 w-4 text-red-600" />
                  Alertas de Despesas
                </h3>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4 pl-6">
                  <div className="space-y-2">
                    <p className="text-sm text-gray-600">Vencimento (dias antes)</p>
                    <div className="flex gap-2">
                      {configAlertas?.despesas?.vencimento_dias_antes?.map((dias) => (
                        <Badge key={dias} variant="secondary">{dias}d</Badge>
                      ))}
                    </div>
                  </div>
                  <div className="space-y-2">
                    <p className="text-sm text-gray-600">Atraso (dias após)</p>
                    <div className="flex gap-2">
                      {configAlertas?.despesas?.atraso_dias_apos?.map((dias) => (
                        <Badge key={dias} variant="destructive">{dias}d</Badge>
                      ))}
                    </div>
                  </div>
                </div>
              </div>

              {/* Contratos */}
              <div>
                <h3 className="font-semibold mb-3 flex items-center gap-2">
                  <CreditCard className="h-4 w-4 text-purple-600" />
                  Alertas de Contratos
                </h3>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4 pl-6">
                  <div className="space-y-2">
                    <p className="text-sm text-gray-600">Renovação (dias antes)</p>
                    <div className="flex gap-2">
                      {configAlertas?.contratos?.renovacao_dias_antes?.map((dias) => (
                        <Badge key={dias} variant="secondary">{dias}d</Badge>
                      ))}
                    </div>
                  </div>
                  <div className="space-y-2">
                    <p className="text-sm text-gray-600">Reajuste (dias antes)</p>
                    <div className="flex gap-2">
                      {configAlertas?.contratos?.reajuste_dias_antes?.map((dias) => (
                        <Badge key={dias} variant="secondary">{dias}d</Badge>
                      ))}
                    </div>
                  </div>
                </div>
              </div>

              {/* Resumos */}
              <div>
                <h3 className="font-semibold mb-3 flex items-center gap-2">
                  <Bell className="h-4 w-4 text-blue-600" />
                  Resumos Periódicos
                </h3>
                <div className="grid grid-cols-3 gap-4 pl-6">
                  <div className="flex items-center justify-between p-3 bg-gray-50 rounded-lg">
                    <span className="text-sm">Diário</span>
                    {configAlertas?.resumo_diario ? (
                      <CheckCircle className="h-4 w-4 text-green-600" />
                    ) : (
                      <span className="text-gray-400">✕</span>
                    )}
                  </div>
                  <div className="flex items-center justify-between p-3 bg-gray-50 rounded-lg">
                    <span className="text-sm">Semanal</span>
                    {configAlertas?.resumo_semanal ? (
                      <CheckCircle className="h-4 w-4 text-green-600" />
                    ) : (
                      <span className="text-gray-400">✕</span>
                    )}
                  </div>
                  <div className="flex items-center justify-between p-3 bg-gray-50 rounded-lg">
                    <span className="text-sm">Mensal</span>
                    {configAlertas?.resumo_mensal ? (
                      <CheckCircle className="h-4 w-4 text-green-600" />
                    ) : (
                      <span className="text-gray-400">✕</span>
                    )}
                  </div>
                </div>
              </div>
            </CardContent>
          </Card>
        </TabsContent>

        {/* ABA DE MÉTODOS DE PAGAMENTO */}
        <TabsContent value="metodos" className="space-y-6">
          <div className="flex items-center justify-between">
            <div>
              <h2 className="text-xl font-semibold">Métodos de Pagamento</h2>
              <p className="text-sm text-gray-500">Métodos disponíveis para receitas e despesas</p>
            </div>
            <Button variant="outline" onClick={handleCriarMetodosPadrao}>
              <CheckCircle className="h-4 w-4 mr-2" />
              Criar Métodos Padrão
            </Button>
          </div>

          <Card>
            <CardContent className="pt-6">
              {metodosPagamento.length === 0 ? (
                <div className="text-center py-12">
                  <CreditCard className="h-16 w-16 mx-auto mb-4 text-gray-300" />
                  <p className="text-gray-500 mb-4">Nenhum método de pagamento cadastrado</p>
                  <Button onClick={handleCriarMetodosPadrao}>
                    Criar Métodos Padrão
                  </Button>
                </div>
              ) : (
                <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
                  {metodosPagamento.map((metodo) => (
                    <Card key={metodo._id || metodo.tipo} className="hover:shadow-md transition-shadow">
                      <CardContent className="pt-6">
                        <div className="flex items-center justify-between">
                          <div className="flex items-center gap-3">
                            <div className="h-10 w-10 bg-purple-100 rounded-lg flex items-center justify-center">
                              <CreditCard className="h-5 w-5 text-purple-600" />
                            </div>
                            <div>
                              <p className="font-semibold text-gray-900">{metodo.nome}</p>
                              {metodo.ativo ? (
                                <Badge variant="secondary" className="mt-1 bg-green-100 text-green-800">
                                  Ativo
                                </Badge>
                              ) : (
                                <Badge variant="secondary">Inativo</Badge>
                              )}
                            </div>
                          </div>
                        </div>
                      </CardContent>
                    </Card>
                  ))}
                </div>
              )}
            </CardContent>
          </Card>
        </TabsContent>
      </Tabs>

      {/* Modal de Criar/Editar Categoria */}
      <Dialog open={categoriaModalOpen} onOpenChange={(open) => {
        setCategoriaModalOpen(open);
        if (!open) resetCategoriaForm();
      }}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>
              {modoEdicao ? 'Editar Categoria' : 'Nova Categoria'}
            </DialogTitle>
            <DialogDescription>
              {modoEdicao ? 'Atualize as informações da categoria' : 'Crie uma nova categoria financeira'}
            </DialogDescription>
          </DialogHeader>

          <form onSubmit={handleSubmitCategoria} className="space-y-4">
            <div className="space-y-2">
              <Label htmlFor="nome">Nome da Categoria *</Label>
              <Input
                id="nome"
                value={categoriaFormData.nome}
                onChange={(e) => setCategoriaFormData({ ...categoriaFormData, nome: e.target.value })}
                placeholder="Ex: Aluguel, Marketing"
                required
              />
            </div>

            <div className="space-y-2">
              <Label htmlFor="tipo">Tipo *</Label>
              <Select
                value={categoriaFormData.tipo}
                onValueChange={(value) => setCategoriaFormData({ ...categoriaFormData, tipo: value })}
              >
                <SelectTrigger>
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="receita">Receita</SelectItem>
                  <SelectItem value="despesa">Despesa</SelectItem>
                </SelectContent>
              </Select>
            </div>

            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label htmlFor="cor">Cor</Label>
                <Select
                  value={categoriaFormData.cor}
                  onValueChange={(value) => setCategoriaFormData({ ...categoriaFormData, cor: value })}
                >
                  <SelectTrigger>
                    <div className="flex items-center gap-2">
                      <div
                        className="h-4 w-4 rounded"
                        style={{ backgroundColor: categoriaFormData.cor }}
                      />
                      <SelectValue />
                    </div>
                  </SelectTrigger>
                  <SelectContent>
                    {coresDisponiveis.map((cor) => (
                      <SelectItem key={cor.value} value={cor.value}>
                        <div className="flex items-center gap-2">
                          <div
                            className="h-4 w-4 rounded"
                            style={{ backgroundColor: cor.value }}
                          />
                          {cor.label}
                        </div>
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>

              <div className="space-y-2">
                <Label htmlFor="icone">Ícone</Label>
                <Select
                  value={categoriaFormData.icone}
                  onValueChange={(value) => setCategoriaFormData({ ...categoriaFormData, icone: value })}
                >
                  <SelectTrigger>
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    {iconesDisponiveis.map((icone) => (
                      <SelectItem key={icone} value={icone}>
                        {icone}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
            </div>

            <div className="space-y-2">
              <Label htmlFor="descricao">Descrição</Label>
              <Input
                id="descricao"
                value={categoriaFormData.descricao}
                onChange={(e) => setCategoriaFormData({ ...categoriaFormData, descricao: e.target.value })}
                placeholder="Breve descrição (opcional)"
              />
            </div>

            <DialogFooter>
              <Button type="button" variant="outline" onClick={() => setCategoriaModalOpen(false)}>
                Cancelar
              </Button>
              <Button type="submit">
                {modoEdicao ? 'Atualizar' : 'Criar'} Categoria
              </Button>
            </DialogFooter>
          </form>
        </DialogContent>
      </Dialog>

      {/* Modal de Configurar Alertas */}
      <Dialog open={alertasModalOpen} onOpenChange={setAlertasModalOpen}>
        <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle>Configurar Alertas</DialogTitle>
            <DialogDescription>
              Personalize quando e como você quer ser notificado
            </DialogDescription>
          </DialogHeader>

          <form onSubmit={handleSubmitAlertas} className="space-y-6">
            {/* Receitas */}
            <div className="space-y-4">
              <h3 className="font-semibold">Alertas de Receitas</h3>
              <div className="space-y-2 pl-4">
                <Label>Vencimento (dias antes)</Label>
                <p className="text-xs text-gray-500">Quando alertar sobre receitas vencendo</p>
                <div className="flex gap-2">
                  {[1, 3, 5, 7, 10, 15].map((dias) => (
                    <Button
                      key={dias}
                      type="button"
                      variant={alertasFormData.receitas.vencimento_dias_antes.includes(dias) ? 'default' : 'outline'}
                      size="sm"
                      onClick={() => {
                        const atual = alertasFormData.receitas.vencimento_dias_antes;
                        const novo = atual.includes(dias)
                          ? atual.filter(d => d !== dias)
                          : [...atual, dias].sort((a, b) => a - b);
                        setAlertasFormData({
                          ...alertasFormData,
                          receitas: { ...alertasFormData.receitas, vencimento_dias_antes: novo }
                        });
                      }}
                    >
                      {dias}d
                    </Button>
                  ))}
                </div>
              </div>
            </div>

            {/* Resumos */}
            <div className="space-y-4">
              <h3 className="font-semibold">Resumos Periódicos</h3>
              <div className="space-y-3 pl-4">
                <div className="flex items-center justify-between">
                  <div>
                    <Label>Resumo Diário</Label>
                    <p className="text-xs text-gray-500">Receba um resumo todos os dias</p>
                  </div>
                  <Switch
                    checked={alertasFormData.resumo_diario}
                    onCheckedChange={(checked) => setAlertasFormData({ ...alertasFormData, resumo_diario: checked })}
                  />
                </div>

                <div className="flex items-center justify-between">
                  <div>
                    <Label>Resumo Semanal</Label>
                    <p className="text-xs text-gray-500">Receba um resumo toda semana</p>
                  </div>
                  <Switch
                    checked={alertasFormData.resumo_semanal}
                    onCheckedChange={(checked) => setAlertasFormData({ ...alertasFormData, resumo_semanal: checked })}
                  />
                </div>

                <div className="flex items-center justify-between">
                  <div>
                    <Label>Resumo Mensal</Label>
                    <p className="text-xs text-gray-500">Receba um resumo todo mês</p>
                  </div>
                  <Switch
                    checked={alertasFormData.resumo_mensal}
                    onCheckedChange={(checked) => setAlertasFormData({ ...alertasFormData, resumo_mensal: checked })}
                  />
                </div>
              </div>
            </div>

            <DialogFooter>
              <Button type="button" variant="outline" onClick={() => setAlertasModalOpen(false)}>
                Cancelar
              </Button>
              <Button type="submit">
                Salvar Configurações
              </Button>
            </DialogFooter>
          </form>
        </DialogContent>
      </Dialog>

      {/* Dialog de Confirmação de Exclusão */}
      <AlertDialog open={deleteDialogOpen} onOpenChange={setDeleteDialogOpen}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Tem certeza?</AlertDialogTitle>
            <AlertDialogDescription>
              Esta ação removerá a categoria <strong>{categoriaSelecionada?.nome}</strong>.
              {categoriaSelecionada?.uso_count > 0 && (
                <span className="block mt-2 text-yellow-600">
                  Atenção: Esta categoria está em uso em {categoriaSelecionada.uso_count} registros.
                  Ela será desativada ao invés de deletada.
                </span>
              )}
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Cancelar</AlertDialogCancel>
            <AlertDialogAction onClick={handleDeleteCategoria} className="bg-red-600 hover:bg-red-700">
              Sim, Remover
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
};

export default Configuracoes;
